<template>
  <div v-show="!isReadyToShow">
    <LoadingSpinner />
  </div>
  <section v-show="isReadyToShow">
    <PrimaryEmailVerificationWrapperComponent
      notification-context="HomeComponent"
      :email="email"
      :show-dialog="toggleVerifyEmail"
      @close-dialog="closeVerifyDialog()"
    />
    <article class="tile-wrapper">
      <BackupOverviewComponent
        v-if="backupEnabled"
        :key="`backupKey-${reloadKey}`"
        :subscription-status-type="backupStatus"
        @tile-ready="updateTilesReadyCount()"
      />
      <SecurityOverviewComponent
        :key="`securityKey-${reloadKey}`"
        :subscription-status-type="securityStatus"
        :needs-to-validate-email="needsToValidateEmail"
        :user-email="email"
        @tile-ready="updateTilesReadyCount()"
        @show-verify-email-dialog="toggleVerifyEmailDialog()"
      />
      <AllStateOverviewComponent
        v-if="allStateEnabled"
        :key="`allstateKey-${reloadKey}`"
        @tile-ready="updateTilesReadyCount()"
      />
      <SubscriptionOverviewComponent
        v-if="!isEmptySubscriptions"
        :key="`subscriptionKey-${reloadKey}`"
        @subscription-status="updateSubscriptionStatus"
        @tile-ready="updateTilesReadyCount()"
        @is-empty-subs="setIsEmptySubscriptions"
      />
    </article>
  </section>
  <ManageResellerControlRequestComponent
    :show-dialog="displayManageResellerControlRequest"
    :request-data="pendingManageRequest"
    @close-modal="toggleManageResellerControlRequest"
    @save-modal="saveManageResellerControlRequest"
  />
</template>

<script lang="ts">
import { defineComponent, ref, onBeforeMount, computed, onMounted, watch } from "vue";
import { logEvent } from "@/common/logger";
import BackupOverviewComponent from "@/components/Home/BackupOverview.vue";
import SecurityOverviewComponent from "@/components/Home/SecurityOverview.vue";
import SubscriptionOverviewComponent from "@/components/Home/SubscriptionOverview.vue";
import PrimaryEmailVerificationWrapperComponent from "@/components/shared/PrimaryEmailVerificationWrapper.vue";
import AllStateOverviewComponent from "@/components/Home/AllStateOverviewComponent.vue";
import { StatusType } from "@/globalEnums";
import { useUserStore } from "@/stores/user";
import { hasOnlyBestBuyS2 } from "@/common/bestBuy";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import { useNotificationsStore } from "@/stores/notifications";
import { useSecurityStore } from "@/stores/security";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import ManageResellerControlRequestComponent from "@/components/shared/dialogs/ManageResellerControlRequest/ManageResellerControlRequest.vue";
import { unifiedApi } from "@/common";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import {
  IGetPendingRequestToManageResponse,
  ISetLanguagePreferenceRequest,
} from "@/common/api/unifiedPortal/interfaces";
import { getUserDetailsFromStore } from "@/common/userDetails";

const name = "HomeComponent";
const everDisplayingTilesCount = 2;

export default defineComponent({
  name,
  components: {
    BackupOverviewComponent,
    SubscriptionOverviewComponent,
    SecurityOverviewComponent,
    AllStateOverviewComponent,
    LoadingSpinner,
    PrimaryEmailVerificationWrapperComponent,
    ManageResellerControlRequestComponent,
  },
  setup() {
    logEvent("created", name);
    const backupStatus = ref(StatusType.normal);
    const userStore = useUserStore();
    const email = ref<string>("");
    const securityStatus = ref(StatusType.normal);
    const allStateEnabled = ref<boolean>(false);
    const backupEnabled = ref<boolean>(false);
    const tilesReadyCount = ref<number>(0);
    const toggleVerifyEmail = ref<boolean>(false);
    const notificationsStore = useNotificationsStore();
    const isEmptySubscriptions = ref<boolean>(false);
    const reloadKey = ref<number>(0);
    const displayManageResellerControlRequest = ref<boolean>(false);
    const pendingManageRequest = ref<IGetPendingRequestToManageResponse>();

    onBeforeMount(async () => {
      const bbyS2Only = await hasOnlyBestBuyS2();
      allStateEnabled.value = !bbyS2Only;
      backupEnabled.value = !bbyS2Only;
      await openManageResellerControlRequest();
    });

    const user = computed(() => {
      return userStore.currentUser;
    });

    const userDetails = computed(() => {
      return userStore.userDetails;
    });

    const needsToValidateEmail = computed(
      () => user.value?.subscriberInfo?.hasPendingWebrootLicenses && !user.value?.subscriberInfo?.hasValidatedEmail
    );

    onMounted(async () => {
      logEvent("mounted", name, user.value);
      email.value = user.value?.email ?? "";
      notificationsStore.clearNotifications();
      if (needsToValidateEmail.value) {
        notificationsStore.addNotification({ type: "PrimaryEmailVerificationWebroot" });
      } else {
        notificationsStore.deleteNotificationByType("PrimaryEmailVerificationWebroot");
      }
      // Checking the languageCode because just after Account setup
      // Check should happen based on latest value
      await getUserDetailsFromStore(true);
      if (!userDetails.value?.languageCode) {
        try {
          //Setting the default email communication language as english
          const request: ISetLanguagePreferenceRequest = {
            languageCode: localStorage.getItem("selectedLanguage") || "en",
          };
          await unifiedApi.setLanguagePreference(request);
          await getUserDetailsFromStore(true);
        } catch (error) {
          handleApiError(error as AxiosError, true);
        }
      }

      if (userStore.twoFactorAuthAddedFromFirstLogin) {
        notificationsStore.addNotification({ type: "SetTwoStepVerificationSuccess" });
        userStore.twoFactorAuthAddedFromFirstLogin = false;
      }
    });

    function updateSubscriptionStatus(type: string, status: StatusType) {
      if (type === "backup") {
        backupStatus.value = status;
      } else if (type === "security") {
        securityStatus.value = status;
      }
    }
    function toggleVerifyEmailDialog() {
      toggleVerifyEmail.value = !toggleVerifyEmail.value;
    }
    async function closeVerifyDialog() {
      toggleVerifyEmail.value = false;
      await refreshHome();
    }

    async function refreshHome() {
      isEmptySubscriptions.value = false;
      tilesReadyCount.value = 0;
      await Promise.all([
        useSubscriptionsStore().forceRefreshAndGetSubscriptions(),
        useSecurityStore().forceRefresh(),
        getUserDetailsFromStore(true),
        reloadSubscriberInfo(),
      ]);
      reloadKey.value++;
    }

    const totalTilesToDisplay = computed(
      () => Number(allStateEnabled.value) + Number(backupEnabled.value) + everDisplayingTilesCount
    );
    const isReadyToShow = computed(() => tilesReadyCount.value >= totalTilesToDisplay.value);

    function updateTilesReadyCount() {
      tilesReadyCount.value++;
    }

    function setIsEmptySubscriptions(isEmpty: boolean) {
      isEmptySubscriptions.value = isEmpty;
    }

    async function openManageResellerControlRequest() {
      try {
        if (userStore.displayedManageResellerControlRequest) return;
        userStore.displayedManageResellerControlRequest = true;
        pendingManageRequest.value = (await unifiedApi.getPendingRequestToManageAsync())?.data;
        if (pendingManageRequest.value && pendingManageRequest.value.resellerId > 0)
          toggleManageResellerControlRequest();
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function toggleManageResellerControlRequest() {
      displayManageResellerControlRequest.value = !displayManageResellerControlRequest.value;
    }

    async function saveManageResellerControlRequest() {
      toggleManageResellerControlRequest();
      await refreshHome();
    }

    const language = computed(() => userStore.selectedLanguage);
    watch(language, async () => {
      await refreshHome();
    });

    return {
      updateSubscriptionStatus,
      securityStatus,
      backupStatus,
      allStateEnabled,
      backupEnabled,
      isReadyToShow,
      updateTilesReadyCount,
      setIsEmptySubscriptions,
      email,
      isEmptySubscriptions,
      needsToValidateEmail,
      toggleVerifyEmailDialog,
      closeVerifyDialog,
      toggleVerifyEmail,
      reloadKey,
      displayManageResellerControlRequest,
      pendingManageRequest,
      saveManageResellerControlRequest,
      toggleManageResellerControlRequest,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
